<template>
  <div class="product-detail-container">
    <!-- 返回按钮 -->
    <div class="back-header">
      <el-button type="text" @click="goBack" icon="el-icon-arrow-left">
        返回商品列表
      </el-button>
    </div>

    <!-- 基本信息 -->
    <el-card class="detail-card" v-loading="loading">
      <div slot="header" class="card-header">
        <span>基本信息</span>
        <el-tag :type="productInfo.isOnSale ? 'success' : 'info'" size="small">
          {{ productInfo.isOnSale ? '已上架' : '已下架' }}
        </el-tag>
      </div>

      <div class="info-section">
        <div class="product-image-section">
          <el-image
              v-if="productInfo.imageId"
              :src="getImageUrl(productInfo.imageId)"
              fit="cover"
              class="product-main-image"
              :preview-src-list="[getImageUrl(productInfo.imageId)]"
          >
            <div slot="error" class="image-error">
              <i class="el-icon-picture-outline"></i>
            </div>
          </el-image>
          <div v-else class="image-placeholder">
            <i class="el-icon-picture-outline"></i>
            <span>暂无图片</span>
          </div>
        </div>

        <div class="product-base-info">
          <div class="info-row">
            <span class="label">商品ID：</span>
            <span class="value">{{ productInfo.id }}</span>
          </div>
          <div class="info-row">
            <span class="label">商品名称：</span>
            <span class="value name">{{ productInfo.name }}</span>
          </div>
          <div class="info-row">
            <span class="label">商品类型：</span>
            <el-tag :type="getTypeColor(productInfo.type)" size="small">
              {{ getTypeText(productInfo.type) }}
            </el-tag>
          </div>
          <div class="info-row">
            <span class="label">所属分类：</span>
            <span class="value">{{ getCategoryName(productInfo.categoryId) }}</span>
          </div>
          <div class="info-row">
            <span class="label">所属店铺：</span>
            <span class="value">{{ productInfo.merchantName || '--' }}</span>
          </div>
          <div class="info-row">
            <span class="label">售价：</span>
            <span class="value price">¥{{ formatPrice(productInfo.price) }}</span>
          </div>
          <div class="info-row">
            <span class="label">原价：</span>
            <span class="value original-price">¥{{ formatPrice(productInfo.originalPrice) }}</span>
          </div>
          <div class="info-row">
            <span class="label">库存：</span>
            <span class="value">{{ productInfo.stock || 0 }}</span>
          </div>
          <div class="info-row">
            <span class="label">销量：</span>
            <span class="value">{{ productInfo.salesCount || 0 }}</span>
          </div>
          <div class="info-row">
            <span class="label">浏览次数：</span>
            <span class="value">{{ productInfo.viewCount || 0 }}</span>
          </div>
          <div class="info-row">
            <span class="label">排序序号：</span>
            <span class="value">{{ productInfo.sortOrder || 0 }}</span>
          </div>
          <div class="info-row" v-if="productInfo.commissionRate !== null && productInfo.commissionRate !== undefined">
            <span class="label">佣金比例：</span>
            <span class="value">{{ productInfo.commissionRate }}%</span>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 商品描述 -->
    <el-card class="detail-card" v-if="productInfo.description">
      <div slot="header" class="card-header">
        <span>商品描述</span>
      </div>
      <div class="description-content">
        {{ productInfo.description }}
      </div>
    </el-card>

    <!-- 购买限制 -->
    <el-card class="detail-card" v-if="productInfo.minPurchaseQty || productInfo.maxPurchaseQty">
      <div slot="header" class="card-header">
        <span>购买限制</span>
      </div>
      <div class="limit-info">
        <div class="info-row" v-if="productInfo.minPurchaseQty">
          <span class="label">最小购买数量：</span>
          <span class="value">{{ productInfo.minPurchaseQty }}</span>
        </div>
        <div class="info-row" v-if="productInfo.maxPurchaseQty">
          <span class="label">最大购买数量：</span>
          <span class="value">{{ productInfo.maxPurchaseQty }}</span>
        </div>
      </div>
    </el-card>

    <!-- 详情图片 -->
    <el-card class="detail-card" v-if="contentImageUrls && contentImageUrls.length > 0">
      <div slot="header" class="card-header">
        <span>详情图片</span>
      </div>
      <div class="detail-images">
        <el-image
            v-for="(url, index) in contentImageUrls"
            :key="index"
            :src="url"
            fit="contain"
            class="detail-image-item"
            :preview-src-list="contentImageUrls"
        >
          <div slot="error" class="image-error">
            <i class="el-icon-picture-outline"></i>
          </div>
        </el-image>
      </div>
    </el-card>
  </div>
</template>

<script>
import api from "@/utils/api"

export default {
  name: 'ProductDetail',
  data() {
    return {
      loading: false,
      productId: null,
      storeId: null,

      productInfo: {
        id: null,
        name: '',
        merchantId: null,
        merchantName: '',
        categoryId: null,
        price: 0,
        originalPrice: 0,
        description: '',
        salesCount: 0,
        viewCount: 0,
        stock: 0,
        type: null,
        isOnSale: false,
        imageId: null,
        contentImageIds: '',
        minPurchaseQty: null,
        maxPurchaseQty: null,
        commissionRate: null,
        sortOrder: 0
      },

      categoryList: []
    }
  },

  computed: {
    contentImageUrls() {
      if (!this.productInfo.contentImageIds) return []
      return this.productInfo.contentImageIds.split(',')
          .filter(id => id.trim())
          .map(id => this.getImageUrl(id.trim()))
    }
  },

  mounted() {
    this.productId = this.$route.query.id
    this.storeId = this.$route.query.merchantId
    this.initData()
  },

  methods: {
    async initData() {
      if (!this.productId) {
        this.$message.error('缺少商品ID参数')
        this.goBack()
        return
      }

      await Promise.all([
        this.loadCategoryList(),
        this.loadProductDetail()
      ])
    },

    async loadCategoryList() {
      try {
        const res = await api.product_getCategoryList()
        if (res.data.code === '000') {
          this.categoryList = res.data.data.list || res.data.data || []
        }
      } catch (error) {
        console.error('加载分类列表失败:', error)
      }
    },

    async loadProductDetail() {
      this.loading = true
      try {
        const res = await api.product_getProductDetail({ id: this.productId })

        if (res.data.code === '000') {
          const data = res.data.data
          this.productInfo = {
            id: data.id,
            name: data.name || '',
            merchantId: data.merchantId,
            merchantName: data.merchantName || '',
            categoryId: data.categoryId,
            price: data.price || 0,
            originalPrice: data.originalPrice || 0,
            description: data.description || '',
            salesCount: data.salesCount || 0,
            viewCount: data.viewCount || 0,
            stock: data.stock || 0,
            type: data.type,
            isOnSale: data.isOnSale !== undefined ? data.isOnSale : true,
            imageId: data.imageId,
            contentImageIds: data.contentImageIds || '',
            minPurchaseQty: data.minPurchaseQty,
            maxPurchaseQty: data.maxPurchaseQty,
            commissionRate: data.commissionRate,
            sortOrder: data.sortOrder || 0
          }
        } else {
          this.$message.error(res.data.message || '加载商品详情失败')
        }
      } catch (error) {
        console.error('加载商品详情失败:', error)
        this.$message.error('加载商品详情失败')
      } finally {
        this.loading = false
      }
    },

    getCategoryName(categoryId) {
      const category = this.categoryList.find(c => c.id === categoryId)
      return category ? category.name : '未知分类'
    },

    getImageUrl(imageId) {
      if (!imageId) return ''
      return `${process.env.VUE_APP_BASE_API}/image/getImage/${imageId}`
    },

    formatPrice(price) {
      if (price === null || price === undefined) return '0.00'
      return (price / 100).toFixed(2)
    },

    getTypeText(type) {
      const typeMap = {
        'PRODUCT': '实物商品',
        'SERVICE': '服务商品'
      }
      return typeMap[type] || '未知类型'
    },

    getTypeColor(type) {
      const colorMap = {
        'PRODUCT': 'success',
        'SERVICE': 'warning'
      }
      return colorMap[type] || 'info'
    },

    goBack() {
      // 优先使用浏览器后退，如果没有历史记录则跳转到列表页
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push({
          path: '/Index/Product/List',
          query: { merchantId: this.storeId }
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.product-detail-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.back-header {
  margin-bottom: 20px;
}

.detail-card {
  margin-bottom: 20px;

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 16px;
    font-weight: bold;
    color: #303133;
  }
}

.info-section {
  display: flex;
  gap: 30px;

  .product-image-section {
    flex: 0 0 300px;

    .product-main-image {
      width: 300px;
      height: 300px;
      border-radius: 8px;
      border: 1px solid #ebeef5;
    }

    .image-placeholder {
      width: 300px;
      height: 300px;
      border-radius: 8px;
      border: 1px dashed #dcdfe6;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #c0c4cc;
      font-size: 14px;

      i {
        font-size: 48px;
        margin-bottom: 10px;
      }
    }
  }

  .product-base-info {
    flex: 1;

    .info-row {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      padding: 8px 0;
      border-bottom: 1px solid #f5f7fa;

      .label {
        width: 100px;
        color: #909399;
        font-size: 14px;
        flex-shrink: 0;
      }

      .value {
        color: #303133;
        font-size: 14px;

        &.name {
          font-size: 18px;
          font-weight: 600;
        }

        &.price {
          font-size: 20px;
          font-weight: bold;
          color: #f56c6c;
        }

        &.original-price {
          text-decoration: line-through;
          color: #c0c4cc;
        }
      }
    }
  }
}

.description-content {
  padding: 16px;
  line-height: 1.8;
  color: #606266;
  font-size: 14px;
  background-color: #fafbfc;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-all;
}

.limit-info {
  .info-row {
    display: flex;
    align-items: center;
    margin-bottom: 12px;

    .label {
      width: 120px;
      color: #909399;
      font-size: 14px;
    }

    .value {
      color: #303133;
      font-size: 14px;
      font-weight: 500;
    }
  }
}

.detail-images {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  .detail-image-item {
    width: 200px;
    height: 200px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
  }
}

.image-error {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background-color: #f5f7fa;
  color: #c0c4cc;
  font-size: 28px;
}

@media (max-width: 768px) {
  .product-detail-container {
    padding: 12px;
  }

  .info-section {
    flex-direction: column;

    .product-image-section {
      flex: none;
      display: flex;
      justify-content: center;

      .product-main-image,
      .image-placeholder {
        width: 100%;
        max-width: 300px;
        height: auto;
        aspect-ratio: 1;
      }
    }
  }

  .detail-images {
    justify-content: center;

    .detail-image-item {
      width: 100%;
      max-width: 300px;
      height: auto;
      aspect-ratio: 1;
    }
  }
}
</style>